// frontend/src/components/reputation/GmbPostsTab.jsx
// Version: 1.0 (growth-agents-stage1)
// Description: Экран «Вышло за сутки» и управление публикациями Google Business Profile
//              по двум карточкам: Coral Springs и Hallandale Beach.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useUiLanguage, perevesti } from '../../context/UiLanguageContext';
import {
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
  Edit3,
  ExternalLink,
  ShieldCheck,
  ShieldAlert,
  MapPin,
  Globe,
  Clock,
  Check
} from 'lucide-react';

export const GmbPostsTab = ({ profileId: propProfileId, profiles = [], highlightPostId: propHighlightPostId }) => {
  const { lang: rawLang, t: contextT } = useUiLanguage();
  const lang = rawLang || 'ru';
  const t = useCallback((k, v) => (rawLang ? contextT(k, v) : perevesti('ru', k, v)), [rawLang, contextT]);

  // Derive highlightPostId from prop or window location search params
  const highlightPostId = useMemo(() => {
    if (propHighlightPostId) return propHighlightPostId;
    try {
      if (typeof window !== 'undefined' && window.location && window.location.search) {
        return new URLSearchParams(window.location.search).get('postId');
      }
    } catch (_) {}
    return null;
  }, [propHighlightPostId]);

  // Derive profileId from prop or first available profile
  const profileId = useMemo(() => {
    if (propProfileId) return propProfileId;
    if (profiles && profiles.length > 0 && profiles[0].id) return profiles[0].id;
    return null;
  }, [propProfileId, profiles]);

  const [loading, setLoading] = useState(false);
  const [dailyData, setDailyData] = useState({ posts: [], unverified_count: 0 });
  const [locations, setLocations] = useState([]);
  const [canonicalTopics, setCanonicalTopics] = useState([]);
  const [filterUnverifiedOnly, setFilterUnverifiedOnly] = useState(false);

  // Form state for creating a new post
  const [targetOffice, setTargetOffice] = useState('');
  const [postLanguage, setPostLanguage] = useState('en');
  const [selectedTopicObj, setSelectedTopicObj] = useState(null);
  const [postSummary, setPostSummary] = useState('');
  const [postCtaType, setPostCtaType] = useState('LEARN_MORE');
  const [postCtaUrl, setPostCtaUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Patching modal/state
  const [editingPost, setEditingPost] = useState(null);
  const [editSummary, setEditSummary] = useState('');
  const [patching, setPatching] = useState(false);

  // Rewriting modal/state (A2)
  const [rewritingPost, setRewritingPost] = useState(null);
  const [rewriteFeedback, setRewriteFeedback] = useState('');
  const [rewriting, setRewriting] = useState(false);

  // Loading state for single action buttons
  const [actionLoading, setActionLoading] = useState({});

  const setItemLoading = (key, val) => {
    setActionLoading((prev) => ({ ...prev, [key]: val }));
  };

  const fetchDailyPosts = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/v1/reputation/gmb/locations/${profileId}/daily-posts`);
      if (res.data) {
        setDailyData({
          posts: Array.isArray(res.data.posts) ? res.data.posts : [],
          unverified_count: Number(res.data.unverified_count) || 0,
        });
      }
    } catch (err) {
      console.warn('Failed to load daily posts:', err);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  const fetchLocationsAndTopics = useCallback(async () => {
    if (!profileId) return;
    try {
      const [locRes, topRes] = await Promise.all([
        axios.get(`/api/v1/reputation/gmb/locations/${profileId}/all`),
        axios.get('/api/v1/reputation/gmb/topics'),
      ]);
      if (locRes.data?.locations) {
        setLocations(locRes.data.locations);
      }
      if (topRes.data?.topics) {
        setCanonicalTopics(topRes.data.topics);
        if (topRes.data.topics.length > 0 && !selectedTopicObj) {
          const first = topRes.data.topics[0];
          setSelectedTopicObj(first);
          setPostCtaUrl(postLanguage === 'ru' ? first.url_ru : first.url_en);
        }
      }
    } catch (err) {
      console.warn('Failed to load GMB auxiliary data:', err);
    }
  }, [profileId, postLanguage, selectedTopicObj]);

  useEffect(() => {
    if (profileId) {
      fetchDailyPosts();
      fetchLocationsAndTopics();
    }
  }, [profileId, fetchDailyPosts, fetchLocationsAndTopics]);

  useEffect(() => {
    if (highlightPostId && typeof document !== 'undefined') {
      const el = document.getElementById(`gmb-post-${highlightPostId}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightPostId, dailyData.posts]);

  // When language or topic changes, update CTA url to match canonical page
  const handleTopicChange = (topicSlug) => {
    const found = canonicalTopics.find((t) => t.slug === topicSlug);
    if (found) {
      setSelectedTopicObj(found);
      setPostCtaUrl(postLanguage === 'ru' ? found.url_ru : found.url_en);
    }
  };

  const handleLanguageToggle = (newLang) => {
    setPostLanguage(newLang);
    if (selectedTopicObj) {
      setPostCtaUrl(newLang === 'ru' ? selectedTopicObj.url_ru : selectedTopicObj.url_en);
    }
  };

  // Generate draft with AI
  const handleGenerateAI = async () => {
    if (!profileId) return;
    if (!targetOffice) {
      toast.error(t('rep.modal.gmb.toast.select_office') || 'Выберите офис перед генерацией поста');
      return;
    }
    setGenerating(true);
    try {
      const topicName = selectedTopicObj ? (postLanguage === 'ru' ? selectedTopicObj.title_ru : selectedTopicObj.title_en) : '';
      const res = await axios.post(`/api/v1/reputation/gmb/locations/${profileId}/posts/generate?office_key=${encodeURIComponent(targetOffice)}`, {
        topic: topicName,
        language: postLanguage,
      });
      if (res.data?.summary) {
        setPostSummary(res.data.summary);
        if (res.data.cta_url) setPostCtaUrl(res.data.cta_url);
        toast.success(t('rep.modal.gmb.toast.draft_generated'));
      }
    } catch (err) {
      toast.error(t('rep.modal.gmb.toast.draft_failed', { reason: err.response?.data?.detail || err.message }));
    } finally {
      setGenerating(false);
    }
  };

  // Publish / Create Post
  const handleCreatePost = async () => {
    if (!profileId || !postSummary.trim()) {
      toast.error(t('rep.modal.gmb.toast.post_empty'));
      return;
    }
    setSubmitting(true);
    try {
      const topicTitle = selectedTopicObj ? (postLanguage === 'ru' ? selectedTopicObj.title_ru : selectedTopicObj.title_en) : '';
      const res = await axios.post(`/api/v1/reputation/gmb/locations/${profileId}/posts`, {
        summary: postSummary,
        cta_type: postCtaType,
        cta_url: postCtaUrl,
        post_language: postLanguage,
        office_key: targetOffice,
        ai_topic: topicTitle,
      });
      if (res.data?.live_published) {
        toast.success(t('rep.modal.gmb.toast.post_live'));
      } else {
        toast.success(t('rep.modal.gmb.toast.post_copied_open'));
      }
      setPostSummary('');
      fetchDailyPosts();
    } catch (err) {
      toast.error(t('rep.modal.gmb.toast.post_failed', { reason: err.response?.data?.detail || err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  // Verify post (mark as verified by Ekaterina / staff)
  const handleVerify = async (postId) => {
    if (!profileId) return;
    setItemLoading(`verify_${postId}`, true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/locations/${profileId}/posts/${postId}/verify`);
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_verified', { by: res.data.verified_by || 'Staff' }));
        fetchDailyPosts();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setItemLoading(`verify_${postId}`, false);
    }
  };

  // Approve post from needs_review
  const handleApprove = async (postId) => {
    if (!profileId) return;
    setItemLoading(`approve_${postId}`, true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/locations/${profileId}/posts/${postId}/approve`);
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_approved'));
        fetchDailyPosts();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setItemLoading(`approve_${postId}`, false);
    }
  };

  // Delete post from Google
  const handleDelete = async (postId) => {
    if (!profileId) return;
    if (!window.confirm(t('rep.gmb.confirm_delete'))) return;
    setItemLoading(`delete_${postId}`, true);
    try {
      const res = await axios.delete(`/api/v1/reputation/gmb/locations/${profileId}/posts/${postId}`);
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_deleted'));
        fetchDailyPosts();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setItemLoading(`delete_${postId}`, false);
    }
  };

  // Open patch modal
  const handleOpenPatch = (post) => {
    setEditingPost(post);
    setEditSummary(post.summary || '');
  };

  // Submit patch to Google
  const handleSavePatch = async () => {
    if (!profileId || !editingPost) return;
    setPatching(true);
    try {
      const res = await axios.patch(`/api/v1/reputation/gmb/locations/${profileId}/posts/${editingPost.id}`, {
        summary: editSummary,
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_patched'));
        setEditingPost(null);
        fetchDailyPosts();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setPatching(false);
    }
  };

  // Open rewrite modal (A2)
  const handleOpenRewrite = (post) => {
    setRewritingPost(post);
    setRewriteFeedback('');
  };

  // Submit rewrite request to AI & update Google
  const handleRewrite = async () => {
    if (!profileId || !rewritingPost) return;
    setRewriting(true);
    try {
      const res = await axios.post(`/api/v1/reputation/gmb/locations/${profileId}/posts/${rewritingPost.id}/rewrite`, {
        feedback: rewriteFeedback.trim() || undefined
      });
      if (res.data?.success) {
        toast.success(t('rep.gmb.toast_rewritten'));
        setRewritingPost(null);
        setRewriteFeedback('');
        fetchDailyPosts();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setRewriting(false);
    }
  };

  const displayedPosts = useMemo(() => {
    if (!dailyData.posts) return [];
    if (!filterUnverifiedOnly) return dailyData.posts;
    return dailyData.posts.filter((p) => !p.verified_at);
  }, [dailyData.posts, filterUnverifiedOnly]);

  return (
    <div className="space-y-6">
      {/* ── HEADER: «ВЫШЛО ЗА СУТКИ» ────────────────────────────────────────── */}
      <div className="bg-white border border-[#E5E1D6] rounded-2xl p-6 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-serif font-bold text-[#20231F] flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#C8A653]" />
                <span>{t('rep.gmb.daily_title')}</span>
              </h2>
              {dailyData.unverified_count > 0 ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                  <AlertTriangle className="w-3.5 h-3.5 text-[#C8A653]" />
                  <span>{t('rep.gmb.unverified_count', { count: dailyData.unverified_count })}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#0B8B5E]" />
                  <span>{t('rep.gmb.all_verified')}</span>
                </span>
              )}
            </div>
            <p className="text-xs text-[#687077] mt-1 leading-relaxed">
              {t('rep.gmb.daily_subtitle')}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFilterUnverifiedOnly(!filterUnverifiedOnly)}
              className={`h-8 px-3 rounded-lg text-xs font-semibold border transition-colors cursor-pointer ${
                filterUnverifiedOnly
                  ? 'bg-[#20231F] text-white border-[#20231F]'
                  : 'bg-white text-[#20231F] border-[#E5E1D6] hover:bg-[#F9F7F1]'
              }`}
            >
              {filterUnverifiedOnly ? t('rep.gmb.filter_unverified') : t('rep.gmb.filter_all')}
            </button>
            <button
              type="button"
              onClick={fetchDailyPosts}
              disabled={loading}
              className="h-8 px-3 rounded-lg text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] inline-flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-[#687077] ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? t('rep.state.loading') : t('rep.modal.gmb.btn_sync_now')}</span>
            </button>
          </div>
        </div>

        {/* ДВЕ КАРТОЧКИ: CORAL SPRINGS И HALLANDALE BEACH */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-5 border-t border-[#F1EEE4]">
          {locations.map((loc) => {
            const isCoral = loc.office_key === 'coral-springs';
            return (
              <div
                key={loc.id}
                className="p-4 rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] flex items-center justify-between gap-3 shadow-2xs"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FAF4E5] border border-[#E5D7B5] flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-[#C8A653]" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-[#20231F] flex items-center gap-1.5">
                      <span>{isCoral ? t('rep.gmb.card_coral_springs') : t('rep.gmb.card_hallandale_beach')}</span>
                      <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-white border border-[#E5E1D6] text-[#687077]">
                        {loc.office_key}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#687077] mt-0.5">
                      ⭐ {loc.rating ? loc.rating.toFixed(1) : '5.0'} · {loc.reviews_count || 0} {t('rep.modal.gmb.metric.reviews')}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      loc.sync_status === 'synced'
                        ? 'bg-[#E6F6EE] text-[#0B8B5E]'
                        : 'bg-[#FFF4E5] text-[#B76E00]'
                    }`}
                  >
                    {loc.sync_status === 'synced' ? '✓ Synced' : loc.sync_status || 'Pending'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── СПИСОК ПУБЛИКАЦИЙ ЗА СУТКИ ────────────────────────────────────────── */}
      <div className="space-y-4">
        {displayedPosts.length === 0 ? (
          <div className="bg-white border border-[#E5E1D6] rounded-2xl p-8 text-center text-xs text-[#687077]">
            {t('rep.gmb.empty_daily')}
          </div>
        ) : (
          displayedPosts.map((post) => {
            const isLive = post.status === 'published';
            const isNeedsReview = post.status === 'needs_review';
            const isFailed = post.status === 'failed';
            const isVerified = Boolean(post.verified_at);
            const isHighlighted = Boolean(highlightPostId && String(post.id) === String(highlightPostId));

            return (
              <div
                key={post.id}
                id={`gmb-post-${post.id}`}
                data-post-id={post.id}
                data-highlighted={isHighlighted ? "true" : undefined}
                className={`bg-white border rounded-2xl p-5 shadow-xs space-y-3.5 transition-all ${
                  isHighlighted
                    ? 'border-[#C8A653] ring-2 ring-[#C8A653]/40 bg-[#FCFBF8]'
                    : 'border-[#E5E1D6] hover:border-[#D3CEBF]'
                }`}
              >
                {/* Header row: office, date, badges */}
                <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-[#F1EEE4] text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-[#20231F] flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-[#C8A653]" />
                      <span>{post.office_key || '—'}</span>
                    </span>
                    <span className="text-[#687077]">·</span>
                    <span className="text-[#687077]">
                      {post.created_at ? new Date(post.created_at).toLocaleString() : ''}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FCFBF8] border border-[#E5E1D6] text-[#20231F] uppercase">
                      <Globe className="w-2.5 h-2.5 text-[#C8A653]" />
                      <span>{post.post_language || 'EN'}</span>
                    </span>
                    {post.ai_topic && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E]">
                        {post.ai_topic}
                      </span>
                    )}
                  </div>

                  {/* Status Badges */}
                  <div className="flex items-center gap-1.5">
                    {/* Guard result */}
                    {post.guard_passed === false ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FDF2F2] text-[#C93B2B] border border-[#FAD2CF]">
                        <ShieldAlert className="w-3 h-3 text-[#C93B2B]" />
                        <span>{t('rep.gmb.guard_violations_badge', { count: (post.guard_violations || []).length })}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                        <ShieldCheck className="w-3 h-3 text-[#0B8B5E]" />
                        <span>{t('rep.gmb.guard_passed_badge')}</span>
                      </span>
                    )}

                    {/* Honest status badge */}
                    {isLive && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>{t('rep.gmb.status.published')}</span>
                      </span>
                    )}
                    {isNeedsReview && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5]">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{t('rep.gmb.status.needs_review')}</span>
                      </span>
                    )}
                    {isFailed && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-[#FDF2F2] text-[#C93B2B] border border-[#FAD2CF]">
                        <span>{t('rep.gmb.status.failed')}</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Guard violations warning if any */}
                {post.guard_violations && post.guard_violations.length > 0 && (
                  <div className="p-3 rounded-xl bg-[#FFF8F8] border border-[#FAD2CF] text-xs text-[#C93B2B] space-y-1">
                    <div className="font-bold flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>{t('rep.gmb.status.needs_review')}</span>
                    </div>
                    <ul className="list-disc list-inside space-y-0.5 text-[11px] pl-1">
                      {post.guard_violations.map((v, vIdx) => (
                        <li key={vIdx}>{v}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Post Text */}
                <p className="text-xs text-[#20231F] leading-relaxed whitespace-pre-wrap">
                  {post.summary}
                </p>

                {/* CTA URL & Live Post ID */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-2 text-[11px] text-[#687077]">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#20231F]">{post.cta_type || 'LEARN_MORE'}:</span>
                    {/^https:\/\/[^\s/?#]+/i.test(post.cta_url || '') ? (
                      <a
                        href={post.cta_url.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#C8A653] hover:underline inline-flex items-center gap-1"
                      >
                        <span className="truncate max-w-xs">{post.cta_url}</span>
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ) : (
                      <span>—</span>
                    )}
                  </div>

                  {post.google_post_id && (
                    <span className="font-mono text-[10px] text-[#687077]">
                      Google ID: {post.google_post_id}
                    </span>
                  )}
                </div>

                {/* Bottom Actions Row: Verification & API control */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#F1EEE4]">
                  {/* Verification mark badge / button */}
                  <div>
                    {isVerified ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0B8B5E] bg-[#E6F6EE] px-2.5 py-1 rounded-lg border border-[#B7E5D0]">
                        <Check className="w-3.5 h-3.5 text-[#0B8B5E]" />
                        <span>
                          {t('rep.gmb.verified_at', {
                            by: post.verified_by || 'Ekaterina',
                            date: new Date(post.verified_at).toLocaleDateString(),
                          })}
                        </span>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleVerify(post.id)}
                        disabled={actionLoading[`verify_${post.id}`]}
                        className="h-7 px-3 rounded-lg text-xs font-semibold bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5] hover:bg-[#F3E7C9] inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                      >
                        <Check className="w-3 h-3 text-[#C8A653]" />
                        <span>
                          {actionLoading[`verify_${post.id}`]
                            ? t('rep.gmb.btn_verifying')
                            : t('rep.gmb.btn_verify')}
                        </span>
                      </button>
                    )}
                  </div>

                  {/* Actions: Patch in Google, Remove, Approve, Rewrite */}
                  <div className="flex items-center gap-2">
                    {isNeedsReview && (
                      <button
                        type="button"
                        onClick={() => handleApprove(post.id)}
                        disabled={actionLoading[`approve_${post.id}`]}
                        className="h-7 px-3 rounded-lg text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs"
                      >
                        <CheckCircle2 className="w-3 h-3 text-[#C8A653]" />
                        <span>
                          {actionLoading[`approve_${post.id}`]
                            ? t('rep.gmb.btn_approving')
                            : t('rep.gmb.btn_approve')}
                        </span>
                      </button>
                    )}

                    {post.status === 'published' && (
                      <button
                        type="button"
                        onClick={() => handleOpenRewrite(post)}
                        className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-[#FAF4E5] border border-[#E5D7B5] text-[#A97F2E] hover:bg-[#F3E7C9] inline-flex items-center gap-1 transition-colors cursor-pointer"
                        title={t('rep.gmb.btn_rewrite')}
                      >
                        <RefreshCw className="w-3 h-3 text-[#C8A653]" />
                        <span>{t('rep.gmb.btn_rewrite')}</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => handleOpenPatch(post)}
                      className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] inline-flex items-center gap-1 transition-colors cursor-pointer"
                      title={t('rep.gmb.btn_patch')}
                    >
                      <Edit3 className="w-3 h-3 text-[#687077]" />
                      <span>{t('rep.gmb.btn_patch')}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDelete(post.id)}
                      disabled={actionLoading[`delete_${post.id}`]}
                      className="h-7 px-2.5 rounded-lg text-xs font-semibold bg-white border border-[#FAD2CF] text-[#C93B2B] hover:bg-[#FDF2F2] inline-flex items-center gap-1 transition-colors cursor-pointer"
                      title={t('rep.gmb.btn_delete')}
                    >
                      <Trash2 className="w-3 h-3 text-[#C93B2B]" />
                      <span>{t('rep.gmb.btn_delete')}</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── CREATE / DRAFT FORM (CANON TOPICS & AUTO-FILL URL) ───────────────── */}
      <div className="bg-white border border-[#E5E1D6] rounded-2xl p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-[#F1EEE4]">
          <h3 className="text-sm font-serif font-bold text-[#20231F] flex items-center gap-2">
            <Send className="w-4 h-4 text-[#C8A653]" />
            <span>{t('rep.gmb.create_tab_title')}</span>
          </h3>
          <button
            type="button"
            onClick={handleGenerateAI}
            disabled={generating}
            className="h-8 px-3 rounded-lg text-xs font-semibold bg-[#FAF4E5] text-[#A97F2E] border border-[#E5D7B5] hover:bg-[#F3E7C9] inline-flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <Sparkles className={`w-3.5 h-3.5 text-[#C8A653] ${generating ? 'animate-spin' : ''}`} />
            <span>{generating ? t('rep.modal.gmb.posts.btn_generating') : t('rep.modal.gmb.posts.btn_generate')}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Office selector */}
          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.gmb.select_office_label')}
            </label>
            <select
              value={targetOffice}
              onChange={(e) => setTargetOffice(e.target.value)}
              className="w-full h-9 px-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
            >
              <option value="">{t('rep.gmb.select_office_placeholder')}</option>
              <option value="coral-springs">{t('rep.gmb.card_coral_springs')}</option>
              <option value="hallandale-beach">{t('rep.gmb.card_hallandale_beach')}</option>
            </select>
          </div>

          {/* Language selector */}
          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.gmb.select_language_label')}
            </label>
            <div className="grid grid-cols-2 gap-1.5 h-9">
              <button
                type="button"
                onClick={() => handleLanguageToggle('en')}
                className={`rounded-lg text-xs font-semibold border transition-colors cursor-pointer ${
                  postLanguage === 'en'
                    ? 'bg-[#20231F] text-white border-[#20231F]'
                    : 'bg-white text-[#687077] border-[#E5E1D6] hover:bg-[#F9F7F1]'
                }`}
              >
                {t('rep.gmb.language_en')}
              </button>
              <button
                type="button"
                onClick={() => handleLanguageToggle('ru')}
                className={`rounded-lg text-xs font-semibold border transition-colors cursor-pointer ${
                  postLanguage === 'ru'
                    ? 'bg-[#20231F] text-white border-[#20231F]'
                    : 'bg-white text-[#687077] border-[#E5E1D6] hover:bg-[#F9F7F1]'
                }`}
              >
                {t('rep.gmb.language_ru')}
              </button>
            </div>
          </div>

          {/* Canonical topic selector */}
          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.gmb.select_topic_label')}
            </label>
            <select
              value={selectedTopicObj?.slug || ''}
              onChange={(e) => handleTopicChange(e.target.value)}
              className="w-full h-9 px-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
            >
              {canonicalTopics.map((topic) => (
                <option key={topic.slug} value={topic.slug}>
                  {postLanguage === 'ru' ? topic.title_ru : topic.title_en}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Text area */}
        <div>
          <label className="block text-xs font-semibold text-[#20231F] mb-1">
            {t('rep.modal.gmb.posts.content_label')}
          </label>
          <textarea
            rows={4}
            value={postSummary}
            onChange={(e) => setPostSummary(e.target.value)}
            placeholder={t('rep.modal.gmb.posts.content_placeholder')}
            className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
          />
        </div>

        {/* CTA URL destination */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.modal.gmb.posts.cta_button_label')}
            </label>
            <select
              value={postCtaType}
              onChange={(e) => setPostCtaType(e.target.value)}
              className="w-full h-9 px-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
            >
              <option value="LEARN_MORE">{t('rep.modal.gmb.posts.cta_learn_more')}</option>
              <option value="BOOK">{t('rep.modal.gmb.posts.cta_book')}</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#20231F] mb-1">
              {t('rep.modal.gmb.posts.cta_url_label')}
            </label>
            <input
              type="text"
              value={postCtaUrl}
              onChange={(e) => setPostCtaUrl(e.target.value)}
              className="w-full h-9 px-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div>
            {!targetOffice && (
              <span className="text-xs text-[#DC2626] font-medium">
                {t('rep.gmb.select_office_hint')}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleCreatePost}
            disabled={submitting || !postSummary.trim() || !targetOffice}
            className="h-9 px-5 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] disabled:opacity-50 inline-flex items-center gap-2 transition-colors cursor-pointer shadow-xs"
          >
            <Send className="w-3.5 h-3.5 text-[#C8A653]" />
            <span>{submitting ? t('rep.modal.gmb.posts.btn_publishing') : t('rep.gmb.btn_publish_now')}</span>
          </button>
        </div>
      </div>

      {/* ── PATCH POST MODAL (EDIT IN GOOGLE) ─────────────────────────────────── */}
      {editingPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-[#C8A653]" />
              <span>{t('rep.gmb.patch_title')}</span>
            </h3>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.patch_summary_label')}
              </label>
              <textarea
                rows={5}
                value={editSummary}
                onChange={(e) => setEditSummary(e.target.value)}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditingPost(null)}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel')}
              </button>
              <button
                type="button"
                onClick={handleSavePatch}
                disabled={patching || !editSummary.trim()}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] disabled:opacity-50 inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
              >
                <Check className="w-3.5 h-3.5 text-[#C8A653]" />
                <span>{patching ? t('rep.gmb.patch_saving') : t('rep.gmb.patch_save')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REWRITE POST MODAL (A2) ────────────────────────────────────────── */}
      {rewritingPost && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-4">
            <h3 className="text-base font-serif font-bold text-[#20231F] flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-[#C8A653]" />
              <span>{t('rep.gmb.rewrite_title')}</span>
            </h3>

            <div>
              <label className="block text-xs font-semibold text-[#20231F] mb-1">
                {t('rep.gmb.rewrite_feedback_label')}
              </label>
              <textarea
                rows={4}
                value={rewriteFeedback}
                onChange={(e) => setRewriteFeedback(e.target.value)}
                placeholder={t('rep.gmb.rewrite_feedback_placeholder')}
                className="w-full p-3 text-xs rounded-xl border border-[#E5E1D6] bg-[#FCFBF8] text-[#20231F] focus:outline-hidden focus:border-[#C8A653]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRewritingPost(null)}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-white border border-[#E5E1D6] text-[#20231F] hover:bg-[#F9F7F1] cursor-pointer"
              >
                {t('rep.gmb.patch_cancel')}
              </button>
              <button
                type="button"
                onClick={handleRewrite}
                disabled={rewriting}
                className="h-8 px-4 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] disabled:opacity-50 inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
              >
                <Sparkles className={`w-3.5 h-3.5 text-[#C8A653] ${rewriting ? 'animate-spin' : ''}`} />
                <span>{rewriting ? t('rep.gmb.rewrite_submitting') : t('rep.gmb.btn_rewrite')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GmbPostsTab;
